import { useEffect, useMemo, useState } from "react";
import { listFeedFindings, type FeedFinding } from "./actions";
import { downloadCsv } from "./csv";

// Feed review: every problem the latest review found in the vendors' feed files, with how many rows, examples from the vendor's own
// data, and what to ask them to fix. INTERNAL. Export downloads a CSV only: sending anything to a vendor is a deliberate human step.

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const pct = (f: FeedFinding) => (f.total ? `${((100 * f.count) / f.total).toFixed(f.count / f.total < 0.01 ? 2 : 1)}%` : "");

function toCsv(rows: FeedFinding[]) {
  const head = ["vendor", "file", "severity", "problem", "what we found", "rows affected", "of total rows", "examples", "what we ask the vendor", "review date"];
  const lines = rows.map((r) => [r.vendor, r.file, r.severity, r.title, r.detail, r.count, r.total, (r.examples || []).join("; "), r.request, r.run_date]);
  return [head, ...lines].map((l) => l.map(csvCell).join(",")).join("\r\n");
}

export function FeedReviewPanel() {
  const [rows, setRows] = useState<FeedFinding[]>([]);
  const [runDate, setRunDate] = useState("");
  const [vendor, setVendor] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listFeedFindings().then((r) => {
      setLoading(false);
      if (r.ok) { setRows(r.data.rows); setRunDate(r.data.runDate); } else setError(r.error);
    });
  }, []);

  const vendors = useMemo(() => Array.from(new Set(rows.map((r) => r.vendor))).sort(), [rows]);
  const shown = vendor ? rows.filter((r) => r.vendor === vendor) : rows;
  const high = shown.filter((r) => r.severity === "high").length;

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>Feed mistakes</h1>
          <span className="meta">
            {loading ? "Loading…" : `${shown.length} problem${shown.length === 1 ? "" : "s"}${high ? `, ${high} high` : ""}${runDate ? ` · review of ${runDate}` : ""}`}.
            Internal review of the vendors' files. Export downloads a CSV only and sends nothing to anyone.
          </span>
        </div>
        <div className="tabs">
          <button className={!vendor ? "active" : ""} onClick={() => setVendor("")}>All vendors</button>
          {vendors.map((v) => <button key={v} className={vendor === v ? "active" : ""} onClick={() => setVendor(v)}>{v}</button>)}
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
          <button type="button" className="sheet-export" disabled={!shown.length}
                  onClick={() => downloadCsv(`hec-feed-review${vendor ? "-" + vendor.toLowerCase().replace(/[^a-z0-9]/g, "") : ""}-${runDate || "latest"}.csv`, toCsv(shown))}>
            Export {shown.length || ""} as CSV
          </button>
        </div>
      </div>

      <div className="sheet-scroll">
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Vendor</th><th>Severity</th><th>Problem</th><th>How many</th><th>Examples</th><th>What we found</th><th>What we ask the vendor</th></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.docId}>
                <td>{r.vendor}</td>
                <td><span className={`sev sev-${r.severity}`}>{r.severity}</span></td>
                <td className="wrap">{r.title}</td>
                <td className="num">{r.count.toLocaleString()} of {r.total.toLocaleString()} <span className="meta">{pct(r)}</span></td>
                <td className="mono wrap">{(r.examples || []).join(", ")}</td>
                <td className="wrap">{r.detail}</td>
                <td className="wrap">{r.request}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && !loading && !error && (
          <p className="meta" style={{ padding: 16 }}>No findings. Run the feed review (build_feed_reviews.py with --write) to fill this in for this environment.</p>
        )}
      </div>
    </div>
  );
}
